import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import api from "../../../api/axios";

/* Both halves of /admin/bulk/price are superadmin-only — a price is
   estate-wide, so a store-bound admin has no business there. Matching the
   constant in BulkAllocate.jsx and the service's admin roles. */
const SUPERADMIN_ROLE = 1;

/* ── Bulk Price ───────────────────────────────────────────────────────
   Four steps on one page: choose the change, pick the items, preview every
   old → new price, apply. Nothing is written before step 4.

   PRICES ARE THE SAME IN EVERY STORE. Stores differ on stock and
   availability, never on price, so this screen has no store picker and no
   store column — a change here moves the shelf price of the whole chain. The
   scope is repeated at the top of the page, next to the Apply button and
   inside the confirm, because it is the one thing that would be
   catastrophic to get wrong.

   THE STORE COUNT IN THAT SENTENCE IS THE SERVER'S, ALWAYS. This page used
   to prefer a count it cached from the stores list, which returns
   every store to a superadmin while the preview counts only the active ones
   and the bulk write reaches rows in switched-off stores too — three numbers
   for one blast radius, and the one the operator confirmed against was the
   page's own cache. There is no cache now: the banner says "every store"
   until a preview comes back, and thereafter says exactly what the preview
   says, including how many of those stores are switched off.

   Prices are TAX-INCLUSIVE: the number typed is what the customer pays. The
   GST column is back-calculated out of that number and is labelled derived.
   A live OFFER sits between that number and the customer, so where the
   server reports one, the price the customer would actually pay is printed
   under the new price rather than left to be inferred.

   A bulk bin is MANY ROWS in many stores and they can disagree today. The
   server prices every row against its own current number, so a SPREAD
   ("₹80.00–₹100.00 → ₹85.00") is printed wherever they differ instead of one
   row's number standing in for all of them — in the preview's MRP, selling
   price, change and derived-GST cells, and in step 4's receipt, which reports
   what was actually written and so is the last screen that may invent a
   single before-price for a write that moved several shelves. */

/* The `mode` values POST /admin/bulk/price/{preview,apply} accept. These
   strings go over the wire verbatim and must stay identical to PriceModes in
   ledger-service/Api/BulkStockEndpoints.cs — anything else comes back a 400
   bad_mode. `inputs` must match Inputs(mode) there too: it decides how many
   numbers a pasted line carries. */
const MODES = [
  {
    value: "set_price",
    title: "Set selling price",
    hint: "One explicit shelf price per line. MRP is left alone.",
    inputs: 1,
    valueLabel: "Selling price (₹)",
    columns: ["code", "selling price"],
    sample: "2295kg, 349",
  },
  {
    value: "set_mrp",
    title: "Set MRP",
    hint: "One explicit MRP per line. The selling price is left alone — and a new MRP below it is refused.",
    inputs: 1,
    valueLabel: "MRP (₹)",
    columns: ["code", "mrp"],
    sample: "2295kg, 399",
  },
  {
    value: "set_both",
    title: "Set MRP and selling price",
    hint: "Both numbers on one line, MRP first. This is the mode for a new print run — moving MRP and price separately leaves an illegal state in between.",
    inputs: 2,
    valueLabel: "MRP (₹)",
    valueLabel2: "Selling price (₹)",
    columns: ["code", "mrp", "selling price"],
    sample: "2295kg, 399, 349",
  },
  {
    value: "increase_pct",
    title: "Raise selling price by %",
    hint: "Lifts the selling price. MRP never moves — a line that would end up above its MRP is refused, not quietly corrected.",
    inputs: 1,
    valueLabel: "Raise by (%)",
    columns: ["code", "percent"],
    sample: "2295kg, 5",
    caution:
      "MRP is what is printed on the pack, so a raise cannot lift it. Any line whose new price would sit above its MRP comes back blocked — use \"Set MRP and selling price\" for those.",
  },
  {
    value: "decrease_pct",
    title: "Lower selling price by %",
    hint: "Cuts the selling price. MRP never moves.",
    inputs: 1,
    valueLabel: "Lower by (%)",
    columns: ["code", "percent"],
    sample: "2295kg, 5",
  },
];

const STEPS = [
  { n: 1, title: "What changes" },
  { n: 2, title: "Select items" },
  { n: 3, title: "Preview" },
  { n: 4, title: "Apply" },
];

/* headers the Excel reader understands, in priority order */
const ID_KEYS = [
  "productcode", "code", "skucode", "sku",
  "name", "productname", "identifier", "product", "item",
];
const MRP_KEYS     = ["mrp", "newmrp", "maxretailprice"];
const PRICE_KEYS   = ["sellingprice", "newsellingprice", "price", "newprice", "rate"];
const PCT_KEYS     = ["percent", "pct", "percentage", "change"];
const GENERIC_KEYS = ["value", "amount", "new"];

const numberKeysFor = (mode) => {
  if (mode === "set_mrp") return [...MRP_KEYS, ...GENERIC_KEYS];
  if (mode === "increase_pct" || mode === "decrease_pct")
    return [...PCT_KEYS, ...GENERIC_KEYS];
  return [...PRICE_KEYS, ...GENERIC_KEYS];        // set_price
};

const normKey = (k) => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/* Red and amber have no semantic token in milliyo-theme.css, and the literals
   the rest of the portal uses (#dc2626 / #b45309) fall to roughly 2.8:1 on the
   dark green surfaces — unreadable. Same scoped pair BulkAllocate.jsx uses, so
   the two screens warn in exactly the same colours. */
const BULK_CSS = `
.mly-bulk{
  --bulk-danger:#B42318; --bulk-danger-soft:rgba(180,35,24,.09);
  --bulk-warn:#92400E;   --bulk-warn-soft:rgba(146,64,14,.10);
  --bulk-good:#186B4A;   --bulk-good-soft:rgba(24,107,74,.09);
}
html[data-theme="dark"] .mly-bulk{
  --bulk-danger:#FF9A8F; --bulk-danger-soft:rgba(255,154,143,.10);
  --bulk-warn:#F5C36B;   --bulk-warn-soft:rgba(245,195,107,.12);
  --bulk-good:#7BD8A8;   --bulk-good-soft:rgba(123,216,168,.10);
}
.mly-bulk table.mly-price td, .mly-bulk table.mly-price th{
  padding: 6px 9px !important;
  font-size: 12.5px;
  vertical-align: middle;
}
.mly-bulk table.mly-price th{
  font-size: 10.5px;
  letter-spacing: .5px;
  text-transform: uppercase;
  white-space: nowrap;
}
`;

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 8,
  textTransform: "uppercase",
};

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface)",
  color: "var(--mly-text)",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
};

const sectionStyle = {
  border: "1.5px solid var(--mly-border)",
  borderRadius: 14,
  background: "var(--mly-surface)",
  padding: "20px 22px",
  marginBottom: 18,
};

const primaryBtn = (disabled) => ({
  padding: "11px 24px",
  borderRadius: 10,
  border: "none",
  background: disabled ? "var(--mly-surface-3)" : "var(--mly-heading)",
  color: disabled ? "var(--mly-text-muted)" : "var(--mly-on-accent)",
  fontWeight: 700,
  fontSize: 14,
  cursor: disabled ? "not-allowed" : "pointer",
});

const ghostBtn = {
  padding: "11px 20px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  background: "var(--mly-surface-2)",
  color: "var(--mly-text-soft)",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
};

const linkBtn = {
  border: "none",
  background: "none",
  padding: 0,
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--mly-heading)",
  cursor: "pointer",
  textDecoration: "underline",
};

/* every column of digits on this screen lines up */
const numCell = {
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const monoCell = {
  fontFamily: "var(--mly-font-mono, monospace)",
  fontSize: 12,
};

/* ── EVERY PRICE OFF THIS SERVER IS AN INTEGER NUMBER OF PAISE ──
   Node divided by 100 for us; the .NET service does not — it sends raw paise
   longs and prices everything itself. money() is therefore the single place
   that divides, and EVERY number handed to it is a server money field:
   currentMrp / currentSellingPrice / newMrp / newSellingPrice, all four
   *Range pairs, deltaSellingRange, derived.taxable / tax / gross,
   offer.customerPaysNow / After, and on apply beforeMrp / afterMrp /
   beforeSellingPrice / afterSellingPrice and unchanged[].mrp / sellingPrice.
   Nothing the operator TYPES goes through here, and neither does
   deltaPctRange — that is a percentage, not money. */
const money = (paise) =>
  paise === null || paise === undefined || Number.isNaN(Number(paise))
    ? "—"
    : `₹${(Number(paise) / 100).toFixed(2)}`;

const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;

/* a [min, max] pair of PAISE the server sends for a bulk line, and whether
   it spreads */
const spreads = (r) =>
  Array.isArray(r) && r.length === 2 && Number(r[0]) !== Number(r[1]);
const span = (r) => `${money(r[0])}–${money(r[1])}`;

/* "+₹20.00", "−₹5.00", "—" — a signed move, and the same for a spread of
   moves ("−₹5.00 … +₹20.00"). A bulk bin whose stores disagree does not have
   one change: the row its before-price is read off can sit still while
   another store's shelf moves ₹20, so where the server reports a spread the
   Change column prints the spread rather than the representative row's
   number. A cell reading "—" above a price cell that visibly moves is the
   one-row fiction this screen exists to end. */
const signedMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  return `${v > 0 ? "+" : "−"}${money(Math.abs(v))}`;
};
const signedPct = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}%`;
};
/* the colour of a move: a rise and a fall in one line is neither */
const moveTone = (lo, hi) => {
  if (hi > 0 && lo < 0) return "var(--bulk-warn)";
  if (hi > 0) return "var(--bulk-warn)";
  if (lo < 0) return "var(--bulk-good)";
  return "var(--mly-text-muted)";
};

/* ── ERRORS OFF THE NEW SERVICE ──
   The service answers with a REAL HTTP STATUS and a { code, message } body.
   Node's "200 carrying status:400 in the payload" is gone, so nothing here
   may read a status field out of a body — and a bare refusal
   ({ code: "forbidden" }) carries no message at all, which is why the status
   has to supply the sentence when the body cannot. */
const apiError = (e, fallback) => {
  const status = e?.response?.status;
  const message = e?.response?.data?.message;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (!status) return "Could not reach the server.";
  if (status === 401) return "Your session has expired — sign in again.";
  if (status === 403)
    return "Only a super admin may change prices — your account may not.";
  if (status === 503) return "The server is busy — try again in a moment.";
  return fallback;
};

/* ── mode changes and the pasted list ──
   A pasted "2295kg, 349" means ₹349 under "Set selling price" and 349% under
   "Raise selling price by %". The list itself is the expensive thing on this
   screen, so changing the mode does NOT throw it away — but the numbers in it
   have silently changed meaning, and that is said out loud with a one-click
   way to drop them. These two mirror the service's own row parser so the page
   strips exactly the fields it would have read as numbers. */
const NUMERIC_FIELD = /^\s*₹?\s*-?[\d,]+(?:\.\d+)?\s*%?\s*$/;
const TRAILING_NUMBERS = /^(.*\S)\s+-?\d+(?:\.\d+)?(?:\s+-?\d+(?:\.\d+)?)?$/;

const stripLineNumbers = (line) => {
  const text = String(line).trim();
  if (!text) return text;
  let parts = text.split(/[,\t;|]/).map((x) => x.trim()).filter(Boolean);
  if (parts.length === 1) {
    const m = TRAILING_NUMBERS.exec(text);
    return m ? m[1].trim() : text;
  }
  /* never strip the last field standing — a bare numeric product code is an
     identifier, not a price */
  while (parts.length > 1 && NUMERIC_FIELD.test(parts[parts.length - 1])) {
    parts = parts.slice(0, -1);
  }
  return parts.join(", ");
};

const anyLineCarriesNumbers = (text) =>
  String(text)
    .split(/\r?\n/)
    .some((l) => l.trim() && stripLineNumbers(l) !== l.trim());

const BulkPrice = () => {
  const role = useSelector((state) => state.adminUser.role);
  const canPrice = Number(role) === SUPERADMIN_ROLE;

  /* ── step 1 ── */
  const [mode, setMode] = useState("set_price");
  const [value, setValue] = useState("");
  const [value2, setValue2] = useState("");

  /* ── step 2 ── */
  const [tab, setTab] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [excelRows, setExcelRows] = useState([]);
  const [excelFileName, setExcelFileName] = useState("");
  /* set when a mode change has silently re-read the numbers already in the
     paste box (rupees -> percent, or one column -> two) */
  const [pasteStale, setPasteStale] = useState(false);
  const fileRef = useRef(null);

  /* ── step 3 ── */
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [skipped, setSkipped] = useState({});   // line index -> true
  const [showTax, setShowTax] = useState(false);

  /* Every preview request carries a ticket, and only the newest ticket is
     allowed to paint. Every upstream control calls resetFromStep1(), which
     voids the outstanding ticket — so a reply for a list or a value the
     operator has already changed is dropped instead of repainting steps 3
     and 4 with numbers that no longer match step 1. Without this, Apply
     could post the OLD list under the NEW figure on screen, and the server's
     re-projection would agree with it and write it. */
  const previewTicket = useRef(0);

  /* ── step 4 ── */
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const [error, setError] = useState("");

  const spec = useMemo(
    () => MODES.find((m) => m.value === mode) || MODES[0],
    [mode]
  );

  /* any change upstream invalidates everything downstream — INCLUDING a
     preview request that is still in flight */
  const resetFromStep1 = () => {
    previewTicket.current += 1;
    setPreview(null);
    setSkipped({});
    setResult(null);
    setError("");
  };

  const chooseMode = (next) => {
    if (next === mode) return;
    setMode(next);
    /* A sheet's COLUMNS mean different things to a one- and a two-number
       mode, and the header mapping was decided when it was read, so it is
       dropped rather than reinterpreted.

       The pasted list is kept — it is the expensive thing on this screen and
       an operator comparing two modes should not have to paste it twice. But
       its numbers now mean something else, so that is said out loud (below
       the textarea) with a one-click way to strip them. Silently re-reading
       "2295kg, 349" as 349% is exactly the kind of quiet reinterpretation
       this screen must not do. */
    setExcelRows([]);
    setExcelFileName("");
    setValue("");
    setValue2("");
    setPasteStale(anyLineCarriesNumbers(pasteText));
    resetFromStep1();
  };

  const stripPastedNumbers = () => {
    setPasteText(
      pasteText
        .split(/\r?\n/)
        .map((l) => (l.trim() ? stripLineNumbers(l) : l))
        .join("\n")
    );
    setPasteStale(false);
    resetFromStep1();
  };

  /* ── the identifier list currently in hand ── */
  const pasteLines = useMemo(
    () =>
      pasteText
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean),
    [pasteText]
  );

  const rowCount = tab === "paste" ? pasteLines.length : excelRows.length;
  const canPreview = rowCount > 0;

  /* ── Excel ──
     Read as a raw grid, exactly as BulkAllocate does: sheet_to_json's object
     mode always spends row 1 on column names, so a bare list with no header
     loses its first product without saying a word. Working off the grid lets
     the header row be recognised on its merits and kept as data when it is
     not one. */
  const rowsFromSheet = (sheet) => {
    const grid = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      blankrows: false,
      defval: "",
    });
    if (!grid.length) return [];

    const columns = new Map();
    (Array.isArray(grid[0]) ? grid[0] : []).forEach((cell, i) => {
      const key = normKey(cell);
      if (key && !columns.has(key)) columns.set(key, i);
    });

    const firstOf = (keys) => {
      for (const k of keys) {
        const at = columns.get(k);
        if (at !== undefined) return at;
      }
      return undefined;
    };

    const idAt = firstOf(ID_KEYS);
    const two  = spec.inputs === 2;
    const aAt  = two ? firstOf(MRP_KEYS) : firstOf(numberKeysFor(mode));
    const bAt  = two ? firstOf([...PRICE_KEYS, ...GENERIC_KEYS]) : undefined;

    const hasHeader =
      idAt !== undefined || aAt !== undefined || (two && bAt !== undefined);
    /* An unrecognised header stays in the data on purpose: a stray "Item Name"
       label surfaces once as an unmatched line, where the operator can see and
       ignore it. Dropping it blind would take a real product with it. */
    const body = hasHeader ? grid.slice(1) : grid;

    const cell = (row, at, fallbackAt) => {
      const i = at === undefined ? fallbackAt : at;
      if (i === undefined) return "";
      return String(row[i] ?? "").trim();
    };

    const out = [];
    body.forEach((row) => {
      if (!Array.isArray(row)) return;

      /* positional fallback for a sheet with no headings at all:
         code, number  (code, mrp, selling price for the two-number mode) */
      const code = cell(row, idAt, 0);
      if (!code) return;

      if (two) {
        out.push({
          code,
          mrp: cell(row, aAt, 1),
          sellingPrice: cell(row, bAt, 2),
        });
      } else {
        out.push({ code, value: cell(row, aAt, 1) });
      }
    });

    /* the same code twice is a mistake the server will name; it is not
       collapsed here, because the two lines may carry different prices and
       silently keeping one of them is exactly the wrong answer */
    return out;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onerror = () => {
      setError("Could not read that file — the browser could not open it.");
    };
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!firstSheet) {
          setError("That workbook has no sheets in it.");
          return;
        }
        const rows = rowsFromSheet(firstSheet);
        resetFromStep1();
        setExcelRows(rows);
        setExcelFileName(file.name);
        if (rows.length === 0) {
          setError(
            `No rows found in that sheet. Expected a code column and a ${spec.columns
              .slice(1)
              .join(" and ")} column.`
          );
        }
      } catch (err) {
        setError("Could not read that file — is it a valid .xlsx?");
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const isPctMode = mode === "increase_pct" || mode === "decrease_pct";

  const downloadTemplate = () => {
    const two = spec.inputs === 2;
    const template = two
      ? [
          { code: "2295kg", mrp: 399, sellingPrice: 349 },
          { code: "2291kg", mrp: 85, sellingPrice: 79 },
          { code: "", mrp: "", sellingPrice: "" },
        ]
      : [
          { code: "2295kg", value: isPctMode ? 5 : 349 },
          { code: "2291kg", value: isPctMode ? 5 : 79 },
          { code: "", value: "" },
        ];

    const readme = [
      { Instructions: `THIS SHEET IS FOR THE "${spec.title.toUpperCase()}" MODE. Pick the same mode on screen before uploading it.` },
      { Instructions: `ONE PACK OR ONE BULK BIN PER ROW: ${spec.columns.join(", ")}.` },
      { Instructions: "code = a variant SKU code (most reliable), a product code, or a product name. A name is only accepted when it identifies exactly one priceable line." },
      two
        ? { Instructions: "mrp = the new MRP. sellingPrice = the new shelf price. MRP FIRST — a row with the two swapped comes back blocked, because a selling price above MRP is illegal." }
        : { Instructions: `value = the ${spec.valueLabel.toLowerCase()} for that row. Leave it blank to use the single value typed on screen.` },
      { Instructions: "Column headers are case-insensitive. The header row is optional: a sheet with no headings is read as code in the first column, then the number(s)." },
      { Instructions: "PRICES ARE TAX-INCLUSIVE — type the shelf price the customer pays. GST is worked out from inside it, never added on top." },
      { Instructions: "EVERY PRICE HERE APPLIES TO EVERY STORE. There is no per-store price; stores differ on stock and availability only." },
      { Instructions: "Money is rounded to the nearest paise. Nothing is rounded to the rupee, so the number you type is the number that is written." },
      { Instructions: "Nothing here changes stock or store availability." },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    ws["!cols"] = two
      ? [{ wch: 22 }, { wch: 14 }, { wch: 16 }]
      : [{ wch: 22 }, { wch: 14 }];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 130 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prices");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, `milliyo_bulk_price_${mode}_template.xlsx`);
  };

  /* ── step 3: preview ── */
  /* memoised because both feed useMemo dependency lists below — a fresh []
     on every render would recompute the selection each keystroke */
  const lines = useMemo(() => preview?.lines || [], [preview]);
  const problems = useMemo(() => preview?.problems || [], [preview]);
  const summary = preview?.summary || {};

  const runPreview = async () => {
    /* claim a ticket. resetFromStep1() — which every upstream control calls —
       bumps the counter, so an operator who edits the value or the list while
       this is in flight invalidates this reply before it can land. */
    previewTicket.current += 1;
    const ticket = previewTicket.current;
    const mine = () => previewTicket.current === ticket;

    setPreviewing(true);
    setError("");
    setResult(null);
    try {
      const payload = {
        mode,
        rows: tab === "paste" ? pasteLines : excelRows,
      };
      if (String(value).trim() !== "") payload.value = Number(value);
      if (spec.inputs === 2 && String(value2).trim() !== "") {
        payload.value2 = Number(value2);
      }
      /* the payload IS the preview now — there is no { status, data } wrapper
         to unwrap, and a refusal is a real 4xx rather than a 200 carrying a
         status field */
      const res = await api.post("/admin/bulk/price/preview", payload);
      if (!mine()) return;                     // the operator moved on
      setPreview(res.data || null);
      setSkipped({});
    } catch (e) {
      if (!mine()) return;
      setPreview(null);
      setError(apiError(e, "Could not price those items"));
    } finally {
      /* cleared unconditionally: the Preview button is disabled for the whole
         round trip, so only one request is ever outstanding and this one owns
         the flag whether or not its reply was still wanted */
      setPreviewing(false);
    }
  };

  /* everything the apply will actually write */
  const selected = useMemo(
    () =>
      lines
        .map((l, i) => ({ l, i }))
        .filter(({ l, i }) => !l.blocked && !l.noop && !skipped[i])
        .map(({ l }) => l),
    [lines, skipped]
  );

  const selectedCount = selected.length;
  const packCount = selected.filter((l) => l.pool === "packs").length;
  const binCount = selected.filter((l) => l.pool === "bulk").length;
  const warnCount = selected.filter((l) =>
    (l.issues || []).some((x) => x.level === "warn")
  ).length;

  /* SERVER TRUTH ONLY. The preview reports `stores` as every store the write
     can reach — a bulk-bin price is written into a switched-off store's rows
     too — and `storesActive` as the ones currently trading. Until a preview
     exists there is no number, and "every store" is true without one. */
  const storeTotal = preview?.stores ?? null;
  const storeActive = preview?.storesActive ?? null;
  const storesOff =
    storeTotal !== null && storeActive !== null
      ? Math.max(0, storeTotal - storeActive)
      : 0;
  const storeWord =
    storeTotal !== null ? `all ${plural(storeTotal, "store")}` : "every store";

  /* the sentence that names the blast radius, used under Apply and again in
     the confirm — the two must never be able to disagree */
  const sentence = () => {
    if (!preview) return "Preview a list to see what will change.";
    if (selectedCount === 0) return "No line is ready to write.";
    const parts = [];
    if (packCount) parts.push(plural(packCount, "pack price"));
    if (binCount) parts.push(`${plural(binCount, "bulk bin")} (per kg)`);
    return `${parts.join(" and ")} will be repriced in ${storeWord}.`;
  };

  /* ── step 4: apply ── */
  const runApply = async () => {
    setConfirming(false);
    setApplying(true);
    setError("");
    try {
      const res = await api.post("/admin/bulk/price/apply", {
        mode,
        /* the whole preview line objects, untouched. The server's stale guard
           reads newSellingPrice, newMrp and priceSignature off each one — the
           last is the field Node called binSignature — and re-reads `input`
           for the operator's own numbers. */
        lines: selected,
        note: `Bulk price — ${spec.title}`,
      });
      setResult(res.data || {});
    } catch (e) {
      /* A PARTIAL BATCH IS A 200 with failed[] filled in. The 4xx bodies are
         only { code, message } and never carry applied/failed, so there is no
         per-row detail left to salvage off an error — reading one out of the
         body would only ever produce an empty table. */
      setError(apiError(e, "The price change failed"));
    } finally {
      setApplying(false);
    }
  };

  /* everything the operator has to fix and re-upload, in the same column
     shape the template uses so the sheet can go straight back in.

     EVERY row carries whatever numbers it had. A refusal used to download
     with blank price columns even though the numbers were sitting on the
     matching preview line, so re-uploading the sheet either repriced every
     one of them to the single value on screen or came straight back as "no
     new selling price on this line" — the exact loop the sheet exists to
     avoid. The server now echoes `input` on each refusal and `numbers` on
     each unmatched line, so both are filled in from the source. */
  const failedRows = useMemo(() => {
    const two = spec.inputs === 2;
    const rows = [];

    const push = (code, a, b, reason) =>
      rows.push(
        two
          ? { code, mrp: a ?? "", sellingPrice: b ?? "", reason }
          : { code, value: a ?? "", reason }
      );

    /* every error on the line, not only the first — following one and being
       handed the next on the round trip after is how a 200-line batch turns
       into four */
    const allErrors = (issues) =>
      (issues || [])
        .filter((x) => x.level === "error")
        .map((x) => x.text)
        .join(" · ");

    problems.forEach((p) =>
      push(p.code, p.numbers?.[0], p.numbers?.[1], p.reason || "Not matched")
    );

    lines.forEach((l) => {
      if (!l.blocked) return;
      push(l.code, l.input?.[0], l.input?.[1], allErrors(l.issues) || "Blocked");
    });

    (result?.failed || []).forEach((f) =>
      push(f.code || "", f.input?.[0], f.input?.[1], f.reason || "Failed")
    );

    return rows;
  }, [problems, lines, result, spec.inputs]);

  const downloadFailed = () => {
    if (failedRows.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(failedRows);
    ws["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 80 }];
    const readme = [
      { Instructions: "These rows were NOT repriced. Fix them and re-upload this sheet on the Excel tab." },
      { Instructions: "The 'reason' column is ignored on re-upload — leave it or delete it." },
      { Instructions: "The price columns carry the numbers each row was sent with, so a row refused for a reason that has nothing to do with its price can go straight back in unedited." },
      { Instructions: "A row blocked for sitting above its MRP needs the \"Set MRP and selling price\" mode, which moves both numbers at once." },
      { Instructions: "A row that matched several products needs the exact variant SKU code instead of a name." },
      { Instructions: "A row refused as \"moved since the preview\" simply needs previewing again — someone else changed that price while this batch was open." },
      { Instructions: "A row refused over a live offer is one where the new price would push that offer past the product's max-offer ceiling. Lower the offer first, or do not cut that far." },
    ];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 130 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prices");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_bulk_price_failed.xlsx");
  };

  const startOver = () => {
    previewTicket.current += 1;          // nothing in flight may paint after this
    setPasteText("");
    setExcelRows([]);
    setExcelFileName("");
    setPasteStale(false);
    setValue("");
    setValue2("");
    setPreview(null);
    setSkipped({});
    setResult(null);
    setError("");
  };

  /* ── which step is live ── */
  const currentStep = result ? 4 : preview ? 3 : rowCount > 0 ? 2 : 1;
  const stepReached = (n) => {
    if (n === 1) return true;
    if (n === 2) return true;
    if (n === 3) return !!preview;
    if (n === 4) return !!result;
    return false;
  };

  /* ── small pieces (same shapes as BulkAllocate) ── */
  const countTile = (label, n, color) => (
    <div
      style={{
        flex: 1,
        minWidth: 132,
        border: "1.5px solid var(--mly-border)",
        borderRadius: 12,
        background: "var(--mly-surface-2)",
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: 24,
          fontWeight: 800,
          color,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {n}
      </div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: "var(--mly-text-muted)",
          marginTop: 3,
        }}
      >
        {label}
      </div>
    </div>
  );

  const checkbox = (checked, color) => (
    <div
      style={{
        width: 17,
        height: 17,
        borderRadius: 5,
        border: `2px solid ${checked ? color : "var(--mly-border)"}`,
        background: checked ? color : "var(--mly-surface)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.15s",
      }}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <polyline
            points="2,6 5,9 10,3"
            stroke="var(--mly-on-accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );

  const stepHead = (n, title, sub) => (
    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 800,
          background: "var(--mly-accent-soft)",
          color: "var(--mly-heading)",
          border: "1px solid var(--mly-border)",
        }}
      >
        {n}
      </div>
      <div>
        <h6
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 15,
            color: "var(--mly-heading)",
          }}
        >
          {title}
        </h6>
        {sub && (
          <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
            {sub}
          </span>
        )}
      </div>
    </div>
  );

  const tabButton = (key, label) => (
    <div
      key={key}
      onClick={() => {
        setTab(key);
        resetFromStep1();
      }}
      style={{
        padding: "9px 18px",
        borderRadius: 10,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 700,
        userSelect: "none",
        border:
          tab === key
            ? "2px solid var(--mly-heading)"
            : "1.5px solid var(--mly-border)",
        background:
          tab === key ? "var(--mly-accent-soft)" : "var(--mly-surface-2)",
        color: tab === key ? "var(--mly-heading)" : "var(--mly-text-muted)",
        transition: "all 0.15s",
      }}
    >
      {label}
    </div>
  );

  /* the estate-wide banner. Repeated rather than said once: it is the single
     assumption an operator could carry in wrong. */
  const scopeBanner = (compact) => (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        border: "1.5px solid var(--mly-heading)",
        borderRadius: 12,
        background: "var(--mly-accent-soft)",
        padding: compact ? "10px 14px" : "13px 16px",
        marginBottom: compact ? 0 : 18,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1.2 }}>🏬</span>
      <div style={{ color: "var(--mly-heading)" }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.4 }}>
          Every price here applies to {storeWord}.
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "var(--mly-text-soft)",
            lineHeight: 1.5,
            marginTop: 2,
          }}
        >
          There is no per-store price at MilliyO — stores differ on stock and
          availability only. A change on this page is the shelf price of the
          whole chain, from the moment you apply it.
          {/* The count above is every store the write can REACH, which is not
              the same as the trading estate: a bulk-bin price is written into
              a switched-off store's rows too, so it comes back on already
              repriced. Both numbers come from the preview payload. */}
          {storesOff > 0 && (
            <>
              {" "}
              <strong style={{ fontWeight: 700 }}>
                {storesOff === 1
                  ? "One of them is switched off"
                  : `${storesOff} of them are switched off`}
              </strong>
              {" "}— a bulk-bin price is written into those rows as well, so
              they come back on already at the new price.
            </>
          )}
        </div>
      </div>
    </div>
  );

  const issueChip = (issue, i) => {
    const colour =
      issue.level === "error"
        ? { fg: "var(--bulk-danger)", bg: "var(--bulk-danger-soft)" }
        : issue.level === "warn"
        ? { fg: "var(--bulk-warn)", bg: "var(--bulk-warn-soft)" }
        : { fg: "var(--mly-text-muted)", bg: "var(--mly-surface-2)" };
    return (
      <div
        key={`i-${i}`}
        style={{
          fontSize: 11,
          lineHeight: 1.45,
          color: colour.fg,
          background: colour.bg,
          borderRadius: 6,
          padding: "3px 7px",
          marginBottom: 3,
        }}
      >
        {issue.text}
      </div>
    );
  };

  /* "375.00 → 349.00", with the two numbers aligned and the unchanged half
     of a line greyed rather than repeated in full weight */
  const priceMove = (from, to) => {
    const changed = Number(from) !== Number(to);
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <span
          style={{
            color: "var(--mly-text-muted)",
            textDecoration: changed ? "line-through" : "none",
          }}
        >
          {money(from)}
        </span>
        {changed && (
          <>
            <span style={{ color: "var(--mly-text-muted)" }}> → </span>
            <span style={{ fontWeight: 700, color: "var(--mly-text)" }}>
              {money(to)}
            </span>
          </>
        )}
      </span>
    );
  };

  /* the same cell for a bulk bin whose rows disagree: "₹80.00–₹100.00 → ₹85.00".
     One row's number standing in for every store is precisely the fiction
     this screen exists to end, so where the server reports a spread the cell
     prints the spread. */
  const rangeMove = (fromRange, toRange, from, to) => {
    const wide = spreads(fromRange) || spreads(toRange);
    if (!wide) return priceMove(from, to);
    const left = spreads(fromRange) ? span(fromRange) : money(from);
    const right = spreads(toRange) ? span(toRange) : money(to);
    const changed = left !== right;
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        <span
          style={{
            color: "var(--mly-text-muted)",
            textDecoration: changed ? "line-through" : "none",
          }}
        >
          {left}
        </span>
        {changed && (
          <>
            <span style={{ color: "var(--mly-text-muted)" }}> → </span>
            <span style={{ fontWeight: 700, color: "var(--mly-text)" }}>
              {right}
            </span>
          </>
        )}
      </span>
    );
  };

  const mrpCell = (l) =>
    l.pool === "bulk"
      ? rangeMove(l.currentMrpRange, l.newMrpRange, l.currentMrp, l.newMrp)
      : priceMove(l.currentMrp, l.newMrp);

  const sellCell = (l) => (
    <>
      {l.pool === "bulk"
        ? rangeMove(
            l.currentSellingRange,
            l.newSellingRange,
            l.currentSellingPrice,
            l.newSellingPrice
          )
        : priceMove(l.currentSellingPrice, l.newSellingPrice)}
      {/* a live offer sits between this number and the customer, and nothing
          re-checks the product's max-offer ceiling at checkout — so what the
          customer would actually pay is printed here rather than inferred.
          For a bin whose rows disagree the server quotes the LOWEST new
          price, which is the row that gives most away and the one its
          refusals are measured against; it is labelled so the number can be
          read against the right shelf. */}
      {l.offer && (
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: "var(--bulk-warn)",
            marginTop: 2,
          }}
        >
          customer pays {money(l.offer.customerPaysAfter)}
          {/* TODO(cutover): server does not return offer.worstRow — the
              " (lowest row)" hint that said this figure was quoted off the
              cheapest row of a disagreeing bin has no source field on the new
              payload, so it is left off rather than guessed at. */}
        </div>
      )}
    </>
  );

  /* the Change column. One signed number for a pack or an agreed bin; the
     spread of signed numbers for a bin whose stores will move by different
     amounts, because that line has no single change to report. */
  const changeCell = (l) => {
    /* RANGES ONLY. The scalar deltaSellingPrice and deltaPct are gone; the
       server sends deltaSellingRange (paise) on every line and deltaPctRange
       (percent), the latter NULL when no row on the line has a current price
       to measure against. */
    const r =
      Array.isArray(l.deltaSellingRange) && l.deltaSellingRange.length === 2
        ? l.deltaSellingRange
        : [0, 0];
    const lo = Number(r[0]) || 0;
    const hi = Number(r[1]) || 0;
    const wide = lo !== hi;

    const pr =
      Array.isArray(l.deltaPctRange) && l.deltaPctRange.length === 2
        ? l.deltaPctRange
        : null;
    const showPct = pr && (spreads(pr) || Number(pr[0]) !== 0);

    return (
      <td style={{ ...numCell, fontWeight: 700, color: moveTone(lo, hi) }}>
        {wide ? `${signedMoney(lo)} … ${signedMoney(hi)}` : signedMoney(lo)}
        {showPct && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: "var(--mly-text-muted)",
            }}
          >
            {spreads(pr)
              ? `${signedPct(pr[0])} … ${signedPct(pr[1])}`
              : signedPct(pr[0])}
          </div>
        )}
      </td>
    );
  };

  /* A scoped admin reaching this URL would get a 403 out of both calls and a
     bare "Super admin access required" banner. Say so up front instead. */
  if (!canPrice) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <h5 style={{ margin: "0 0 8px", fontWeight: 700 }}>Bulk Price</h5>
            <div style={{ fontSize: 13.5, color: "var(--mly-text-muted)" }}>
              A price is the same in every store, so changing one here changes
              it for the whole chain. That is limited to a super admin — ask
              one to run the batch for you.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mly-bulk">
      <style>{BULK_CSS}</style>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Bulk Price</h5>
              <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                Move many prices in one pass. Estate-wide — stock and store
                availability are untouched.
              </span>
            </div>
            <button className="btn btn-primary btn-sm widthbtn" onClick={startOver}>
              Start over
            </button>
          </div>

          <div className="card-body">
            {/* ── step rail ── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 18,
              }}
            >
              {STEPS.map((s) => {
                const done = stepReached(s.n) && currentStep > s.n;
                const live = currentStep === s.n && stepReached(s.n);
                return (
                  <div
                    key={s.n}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 14px",
                      borderRadius: 20,
                      fontSize: 12.5,
                      fontWeight: 700,
                      border:
                        live || done
                          ? "1.5px solid var(--mly-heading)"
                          : "1.5px solid var(--mly-border)",
                      background: live
                        ? "var(--mly-accent-soft)"
                        : "var(--mly-surface-2)",
                      color:
                        live || done
                          ? "var(--mly-heading)"
                          : "var(--mly-text-muted)",
                    }}
                  >
                    <span>{done ? "✓" : s.n}</span>
                    <span>{s.title}</span>
                  </div>
                );
              })}
            </div>

            {scopeBanner(false)}

            {error && (
              <div
                style={{
                  background: "var(--bulk-danger-soft)",
                  color: "var(--bulk-danger)",
                  padding: "10px 14px",
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            {/* ══ STEP 1 — WHAT CHANGES ══ */}
            <div style={sectionStyle}>
              {stepHead(
                1,
                "What changes",
                "Pick the kind of change first — it decides what each line has to carry."
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                {MODES.map((m) => {
                  const sel = mode === m.value;
                  return (
                    <div
                      key={m.value}
                      onClick={() => chooseMode(m.value)}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        cursor: "pointer",
                        userSelect: "none",
                        border: sel
                          ? "2px solid var(--mly-heading)"
                          : "1.5px solid var(--mly-border)",
                        background: sel
                          ? "var(--mly-accent-soft)"
                          : "var(--mly-surface-2)",
                        transition: "all 0.15s",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: sel
                            ? "var(--mly-heading)"
                            : "var(--mly-text-soft)",
                        }}
                      >
                        {m.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--mly-text-muted)",
                          marginTop: 4,
                          lineHeight: 1.45,
                        }}
                      >
                        {m.hint}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 190 }}>
                  <label style={labelStyle}>{spec.valueLabel}</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={value}
                    onChange={(e) => {
                      setValue(e.target.value);
                      resetFromStep1();
                    }}
                    placeholder="one value for every line"
                    style={{ ...inputStyle, fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
                {spec.inputs === 2 && (
                  <div style={{ flex: 1, minWidth: 190 }}>
                    <label style={labelStyle}>{spec.valueLabel2}</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      value={value2}
                      onChange={(e) => {
                        setValue2(e.target.value);
                        resetFromStep1();
                      }}
                      placeholder="one value for every line"
                      style={{ ...inputStyle, fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                )}
                <div
                  style={{
                    flex: 2,
                    minWidth: 260,
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12,
                    color: "var(--mly-text-muted)",
                    lineHeight: 1.5,
                  }}
                >
                  Optional. A line that carries its own number always wins;
                  this fills in the rest. "Everything up 5%" is one number and
                  a list of codes.
                </div>
              </div>

              {spec.caution && (
                <div
                  style={{
                    marginTop: 12,
                    border: "1.5px solid var(--bulk-warn)",
                    borderRadius: 12,
                    background: "var(--bulk-warn-soft)",
                    padding: "11px 14px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    lineHeight: 1.5,
                    color: "var(--bulk-warn)",
                  }}
                >
                  {spec.caution}
                </div>
              )}
            </div>

            {/* ══ STEP 2 — SELECT ITEMS ══ */}
            <div style={sectionStyle}>
              {stepHead(
                2,
                "Select items",
                `Paste a list or upload a sheet — ${spec.columns.join(", ")}.`
              )}

              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                {tabButton("paste", "Paste")}
                {tabButton("excel", "Excel")}
              </div>

              {tab === "paste" && (
                <div>
                  <label style={labelStyle}>
                    One pack or bin per line
                    <span
                      style={{
                        fontWeight: 500,
                        letterSpacing: 0,
                        textTransform: "none",
                        color: "var(--mly-text-muted)",
                        marginLeft: 6,
                      }}
                    >
                      ({spec.columns.join(", ")} — comma, tab or a single space)
                    </span>
                  </label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => {
                      setPasteText(e.target.value);
                      /* the operator is editing it now, so it is theirs
                         again — the mode-change warning has been answered */
                      setPasteStale(false);
                      resetFromStep1();
                    }}
                    rows={9}
                    placeholder={
                      spec.inputs === 2
                        ? "2295kg, 399, 349\n2291kg, 85, 79\nWhole Wheat, 72, 68"
                        : `${spec.sample}\n2291kg\nWhole Wheat`
                    }
                    style={{
                      ...inputStyle,
                      fontFamily: "var(--mly-font-mono, monospace)",
                      fontSize: 13,
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--mly-text-muted)",
                      marginTop: 6,
                    }}
                  >
                    {plural(pasteLines.length, "line")} ready. A line with no
                    number falls back to the value in step 1.
                  </div>

                  {/* The list survives a mode change because it is the
                      expensive thing here — but its numbers do not mean the
                      same thing any more, and "2295kg, 349" quietly becoming
                      349% is exactly the reinterpretation this screen must
                      never do in silence. */}
                  {pasteStale && (
                    <div
                      style={{
                        marginTop: 10,
                        border: "1.5px solid var(--bulk-warn)",
                        borderRadius: 10,
                        background: "var(--bulk-warn-soft)",
                        padding: "10px 14px",
                        fontSize: 12.5,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        color: "var(--bulk-warn)",
                      }}
                    >
                      You changed the mode, and these lines still carry the
                      numbers you pasted for the old one. Under &ldquo;
                      {spec.title}&rdquo; they are now read as{" "}
                      {isPctMode
                        ? "PERCENTAGES, not rupees"
                        : `the ${spec.columns.slice(1).join(" and ")}`}
                      . Check them, or drop them and use the value in step 1.
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={stripPastedNumbers}
                          style={{ ...linkBtn, color: "var(--bulk-warn)" }}
                        >
                          Strip the numbers, keep the codes
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "excel" && (
                <div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div
                      onClick={() => fileRef.current && fileRef.current.click()}
                      style={{
                        flex: 1,
                        minWidth: 260,
                        border: "2px dashed var(--mly-heading)",
                        borderRadius: 12,
                        background: excelRows.length
                          ? "var(--mly-surface)"
                          : "var(--mly-accent-soft)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 110,
                        cursor: "pointer",
                        padding: 12,
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 22 }}>📄</div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--mly-text-soft)",
                        }}
                      >
                        {excelFileName || "UPLOAD A SHEET"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>
                        {excelRows.length
                          ? `${plural(excelRows.length, "row")} read`
                          : `XLSX, XLS or CSV — ${spec.columns.join(" / ")}`}
                      </span>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                    </div>

                    <div
                      style={{
                        minWidth: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <button onClick={downloadTemplate} style={ghostBtn}>
                        Download template
                      </button>
                      <span style={{ fontSize: 11.5, color: "var(--mly-text-muted)" }}>
                        The template matches the mode you picked and carries a
                        READ ME tab explaining every column.
                      </span>
                    </div>
                  </div>

                  {excelRows.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 10,
                        background: "var(--mly-surface-2)",
                        padding: "10px 14px",
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                        fontFamily: "var(--mly-font-mono, monospace)",
                      }}
                    >
                      {excelRows
                        .slice(0, 8)
                        .map((r) =>
                          spec.inputs === 2
                            ? `${r.code} · ${r.mrp} · ${r.sellingPrice}`
                            : `${r.code} · ${r.value}`
                        )
                        .join("   |   ")}
                      {excelRows.length > 8
                        ? `   … +${excelRows.length - 8} more`
                        : ""}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <button
                  onClick={runPreview}
                  disabled={!canPreview || previewing}
                  style={primaryBtn(!canPreview || previewing)}
                >
                  {previewing ? "Pricing…" : "Preview every price"}
                </button>
              </div>
            </div>

            {/* ══ STEP 3 — PREVIEW ══ */}
            {preview && (
              <div style={sectionStyle}>
                {stepHead(
                  3,
                  "Preview",
                  "Old price → new price for every line. Nothing has been written yet."
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 14,
                  }}
                >
                  {countTile("Will change", selectedCount, "var(--mly-heading)")}
                  {countTile("Blocked", summary.blocked ?? 0, "var(--bulk-danger)")}
                  {countTile("Already at this price", summary.unchanged ?? 0, "var(--mly-text-muted)")}
                  {countTile("Not matched", problems.length, "var(--bulk-danger)")}
                  {countTile("Worth a look", warnCount, "var(--bulk-warn)")}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--mly-text-muted)",
                      lineHeight: 1.5,
                      flex: 1,
                      minWidth: 260,
                    }}
                  >
                    {preview.rounding} Prices are tax-inclusive: the number
                    below is what the customer pays.
                  </div>
                  <button onClick={() => setShowTax((v) => !v)} style={linkBtn}>
                    {showTax ? "Hide the GST split" : "Show the GST split (derived)"}
                  </button>
                </div>

                {lines.length > 0 && (
                  <div
                    style={{
                      overflowX: "auto",
                      maxHeight: 460,
                      overflowY: "auto",
                      marginBottom: 16,
                    }}
                  >
                    <table className="display table table-bordered mly-price">
                      <thead>
                        <tr>
                          <th style={{ width: 34 }} />
                          <th style={{ minWidth: 42 }}>#</th>
                          <th style={{ minWidth: 190 }}>Item</th>
                          <th style={{ minWidth: 108 }}>Priced</th>
                          {/* the column a mode does not write is labelled as
                              such, because an unstruck number in it means
                              "left alone" and that has to be legible */}
                          <th style={{ minWidth: 140, textAlign: "right" }}>
                            MRP
                            {preview.writes && !preview.writes.mrp && (
                              <div style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 9.5 }}>
                                not touched
                              </div>
                            )}
                          </th>
                          <th style={{ minWidth: 150, textAlign: "right" }}>
                            Selling price
                            {preview.writes && !preview.writes.sell && (
                              <div style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 9.5 }}>
                                not touched
                              </div>
                            )}
                          </th>
                          <th style={{ minWidth: 100, textAlign: "right" }}>Change</th>
                          {showTax && (
                            <>
                              <th style={{ minWidth: 62, textAlign: "right" }}>GST %</th>
                              <th style={{ minWidth: 92, textAlign: "right" }}>Taxable *</th>
                              <th style={{ minWidth: 88, textAlign: "right" }}>GST *</th>
                            </>
                          )}
                          <th style={{ minWidth: 230 }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l, i) => {
                          const off = l.blocked || l.noop || !!skipped[i];
                          const tint = l.blocked
                            ? "var(--bulk-danger-soft)"
                            : l.noop
                            ? "transparent"
                            : undefined;
                          return (
                            <tr
                              key={`${l.product}-${l.skuCode}-${i}`}
                              style={{ background: tint, opacity: off ? 0.62 : 1 }}
                            >
                              <td>
                                {!l.blocked && !l.noop ? (
                                  <div
                                    onClick={() =>
                                      setSkipped((p) => ({ ...p, [i]: !p[i] }))
                                    }
                                    style={{ cursor: "pointer" }}
                                    title="Leave this line out of the batch"
                                  >
                                    {checkbox(!skipped[i], "var(--mly-heading)")}
                                  </div>
                                ) : (
                                  <span style={{ color: "var(--mly-text-muted)" }}>—</span>
                                )}
                              </td>
                              <td style={numCell}>{i + 1}</td>
                              <td>
                                <div style={{ fontWeight: 600 }}>
                                  {l.productName || "—"}
                                </div>
                                <div style={{ ...monoCell, color: "var(--mly-text-muted)" }}>
                                  {l.skuCode || l.productCode || l.code}
                                  {l.label ? ` · ${l.label}` : ""}
                                </div>
                              </td>
                              <td>
                                <div style={{ fontSize: 12 }}>
                                  {l.pool === "packs" ? "Pack" : "Bulk bin"}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10.5,
                                    color: "var(--mly-text-muted)",
                                  }}
                                >
                                  {/* binRows / binChanging / binStores are now
                                      rows / rowsChanging / stores */}
                                  {l.unit}
                                  {l.pool === "bulk" && l.rows
                                    ? ` · ${
                                        l.rowsChanging !== undefined &&
                                        l.rowsChanging !== l.rows
                                          ? `${l.rowsChanging}/${l.rows}`
                                          : l.rows
                                      } row${l.rows === 1 ? "" : "s"}` +
                                      (l.stores > 1 ? ` · ${l.stores} stores` : "")
                                    : ""}
                                </div>
                              </td>
                              <td style={numCell}>{mrpCell(l)}</td>
                              <td style={numCell}>{sellCell(l)}</td>
                              {changeCell(l)}
                              {showTax && (
                                <>
                                  <td style={numCell}>
                                    {l.derived?.taxRatePct
                                      ? `${l.derived.taxRatePct}%`
                                      : "—"}
                                  </td>
                                  {/* TODO(cutover): server does not return
                                      derived.taxableRange / derived.taxRange.
                                      The new `derived` is scalars only —
                                      taxRatePct, taxMode, taxable, tax, gross
                                      and the alt* trio — so a bin whose stores
                                      disagree has no per-row split to spread
                                      here and these two cells show the
                                      representative row's share alone. The
                                      price columns still print the spread. */}
                                  <td style={numCell}>
                                    {l.derived?.taxRatePct
                                      ? money(l.derived.taxable)
                                      : "—"}
                                  </td>
                                  <td style={numCell}>
                                    {l.derived?.taxRatePct
                                      ? money(l.derived.tax)
                                      : "—"}
                                  </td>
                                </>
                              )}
                              <td>
                                {(l.issues || []).map(issueChip)}
                                {skipped[i] && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "var(--mly-text-muted)",
                                    }}
                                  >
                                    Left out of this batch.
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {showTax && (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: "var(--mly-text-muted)",
                      marginBottom: 14,
                      lineHeight: 1.5,
                    }}
                  >
                    * Derived, not entered. Prices are tax-inclusive, so the GST
                    shown is worked back out of the new selling price
                    (taxable = price × 100 ÷ (100 + rate)). Nothing is added on
                    top of what you type.
                  </div>
                )}

                {problems.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>
                      Nothing was priced for these ({problems.length})
                    </label>
                    <div
                      style={{
                        border: "1.5px solid var(--mly-border)",
                        borderRadius: 12,
                        background: "var(--bulk-danger-soft)",
                        padding: "8px 14px",
                        maxHeight: 220,
                        overflowY: "auto",
                        fontSize: 12.5,
                        color: "var(--mly-text-soft)",
                      }}
                    >
                      {problems.map((p, i) => (
                        <div key={`p-${i}`} style={{ padding: "3px 0" }}>
                          <span style={monoCell}>{p.code || "—"}</span>
                          <span style={{ color: "var(--mly-text-muted)" }}>
                            {" "}
                            — {p.reason}
                          </span>
                          {(p.packs || []).length > 0 && (
                            <span style={{ ...monoCell, color: "var(--mly-text-muted)" }}>
                              {" "}
                              {/* { sku, label } now — Node called it skuCode */}
                              [{p.packs.map((k) => k.sku).join(", ")}]
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={downloadFailed}
                      style={{ ...linkBtn, marginTop: 8 }}
                    >
                      Download the rows that need fixing
                    </button>
                  </div>
                )}

                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--mly-text-muted)",
                    background: "var(--mly-surface-2)",
                    border: "1.5px solid var(--mly-border)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  This is a preview. Nothing is saved until you press Apply in
                  step 4 — and blocked lines are never written, whatever you do
                  next.
                </div>
              </div>
            )}

            {/* ══ STEP 4 — APPLY ══ */}
            {preview && (
              <div style={sectionStyle}>
                {stepHead(
                  4,
                  "Apply",
                  "This writes the new prices. Stock and store availability are untouched."
                )}

                {!result ? (
                  <div>
                    <div style={{ marginBottom: 14 }}>{scopeBanner(true)}</div>
                    <div
                      style={{
                        border: "1.5px solid var(--mly-heading)",
                        borderRadius: 12,
                        background: "var(--mly-accent-soft)",
                        padding: "14px 16px",
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: "var(--mly-heading)",
                        marginBottom: 14,
                      }}
                    >
                      {sentence()}
                    </div>
                    <button
                      onClick={() => setConfirming(true)}
                      disabled={selectedCount === 0 || applying}
                      style={primaryBtn(selectedCount === 0 || applying)}
                    >
                      {applying
                        ? "Applying…"
                        : `Reprice ${plural(selectedCount, "line")} in ${storeWord}`}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        marginBottom: 16,
                      }}
                    >
                      {countTile("Repriced", result.summary?.applied ?? 0, "var(--mly-heading)")}
                      {countTile("Raised", result.summary?.raised ?? 0, "var(--bulk-warn)")}
                      {countTile("Lowered", result.summary?.lowered ?? 0, "var(--bulk-good)")}
                      {countTile("Left alone", result.summary?.unchanged ?? 0, "var(--mly-text-muted)")}
                      {countTile("Refused", result.summary?.failed ?? 0, "var(--bulk-danger)")}
                    </div>

                    {(result.applied || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>
                          Written to {storeWord} ({result.applied.length})
                        </label>
                        <div
                          style={{
                            overflowX: "auto",
                            maxHeight: 300,
                            overflowY: "auto",
                          }}
                        >
                          <table className="display table table-bordered mly-price">
                            <thead>
                              <tr>
                                <th style={{ minWidth: 190 }}>Item</th>
                                <th style={{ minWidth: 92 }}>Priced</th>
                                <th style={{ minWidth: 140, textAlign: "right" }}>MRP</th>
                                <th style={{ minWidth: 150, textAlign: "right" }}>Selling price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.applied.map((a, i) => (
                                <tr key={`ap-${i}`}>
                                  <td>
                                    <div style={{ fontWeight: 600 }}>{a.productName}</div>
                                    {/* TODO(cutover): server does not return
                                        applied[].label — the pack's readable
                                        size ("500 g") is not on the applied
                                        line, so the SKU code stands alone
                                        here. The preview table above still
                                        has it. */}
                                    <div style={{ ...monoCell, color: "var(--mly-text-muted)" }}>
                                      {a.skuCode || a.code}
                                    </div>
                                  </td>
                                  <td style={{ fontSize: 11.5 }}>
                                    {/* applied[].unit is gone too, and this
                                        column has to say which shelf the
                                        numbers belong to — so it is derived
                                        from the pool, using the same two words
                                        the preview's own `unit` carries. */}
                                    {a.pool === "bulk" ? "per kg" : "per pack"}
                                    {a.pool === "bulk" && a.rowsWritten > 1
                                      ? ` · ${a.rowsWritten} rows`
                                      : ""}
                                  </td>
                                  <td style={numCell}>
                                    {/* set_mrp and set_both move an MRP on
                                        every row of the bin, and those rows
                                        can start from different MRPs — so the
                                        receipt prints the same spread the
                                        preview did, never one store's number
                                        for a write that moved several */}
                                    {rangeMove(
                                      a.beforeMrpRange,
                                      a.afterMrpRange,
                                      a.beforeMrp,
                                      a.afterMrp
                                    )}
                                  </td>
                                  <td style={numCell}>
                                    {/* the same spread the preview showed: a
                                        bin whose stores disagreed did not have
                                        one before-price, and the receipt must
                                        not invent one */}
                                    {rangeMove(
                                      a.beforeSellingRange,
                                      a.afterSellingRange,
                                      a.beforeSellingPrice,
                                      a.afterSellingPrice
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {(result.unchanged || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>
                          Already at that price ({result.unchanged.length})
                        </label>
                        <div
                          style={{
                            border: "1.5px solid var(--mly-border)",
                            borderRadius: 10,
                            background: "var(--mly-surface-2)",
                            padding: "10px 14px",
                            maxHeight: 180,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {result.unchanged.map((u, i) => (
                            <div key={`un-${i}`} style={{ padding: "2px 0" }}>
                              {u.productName}
                              <span style={{ color: "var(--mly-text-muted)" }}>
                                {" "}
                                — {u.reason || "no change"} ({money(u.sellingPrice)})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(result.failed || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>
                          Refused ({result.failed.length})
                        </label>
                        <div
                          style={{
                            border: "1.5px solid var(--bulk-danger)",
                            borderRadius: 10,
                            background: "var(--bulk-danger-soft)",
                            padding: "10px 14px",
                            maxHeight: 220,
                            overflowY: "auto",
                            fontSize: 12.5,
                            color: "var(--mly-text-soft)",
                          }}
                        >
                          {result.failed.map((f, i) => (
                            <div key={`fl-${i}`} style={{ padding: "2px 0" }}>
                              <span style={monoCell}>{f.code || "—"}</span>
                              <span style={{ color: "var(--mly-text-muted)" }}>
                                {" "}
                                — {f.reason}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {failedRows.length > 0 && (
                        <button onClick={downloadFailed} style={ghostBtn}>
                          Download the rows that need fixing ({failedRows.length})
                        </button>
                      )}
                      <button onClick={startOver} style={primaryBtn(false)}>
                        Reprice another batch
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── confirm ── */}
      {confirming && (
        <>
          <div
            onClick={() => setConfirming(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 500,
              background: "var(--mly-surface)",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 32px",
            }}
          >
            <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700 }}>
              Change the price in {storeWord}?
            </h2>
            <div
              style={{
                fontSize: 14,
                color: "var(--mly-text-soft)",
                lineHeight: 1.6,
                marginBottom: 14,
              }}
            >
              {sentence()} There is no per-store price — this is the shelf price
              of the whole chain, live as soon as you confirm.
            </div>

            <div
              style={{
                border: "1.5px solid var(--bulk-warn)",
                borderRadius: 10,
                background: "var(--bulk-warn-soft)",
                padding: "10px 14px",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.5,
                color: "var(--bulk-warn)",
                marginBottom: 16,
              }}
            >
              {warnCount > 0
                ? `${plural(warnCount, "line")} in this batch carries a warning — a big move, a live offer, a switched-off product, or bin rows that disagree today. Go back and read the Notes column if you have not.`
                : "Read the preview once more if you have not: every number in the Selling price column becomes what customers pay."}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "var(--mly-text-muted)",
                lineHeight: 1.5,
                marginBottom: 20,
              }}
            >
              Stock and store availability are not touched. Blocked lines are
              not written.
              {preview?.writes && !preview.writes.mrp && " MRP is not touched."}
              {preview?.writes && !preview.writes.sell &&
                " The selling price is not touched."}
              {storesOff > 0 &&
                ` ${storesOff} switched-off ${
                  storesOff === 1 ? "store is" : "stores are"
                } included — a bulk bin is repriced there too.`}
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setConfirming(false)} style={ghostBtn}>
                Cancel
              </button>
              <button
                onClick={runApply}
                style={{ ...primaryBtn(false), flex: 1 }}
              >
                Yes, reprice {plural(selectedCount, "line")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkPrice;
